/**
 * Greek string normalization and answer comparison.
 */

import { XP } from '../data/constants.js';

/**
 * Strip Greek tonal marks for lenient comparison.
 * Used only when we want to check "almost correct" — main comparison
 * is always strict.
 */
export function stripAccents(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/ά/g, 'α')
    .replace(/έ/g, 'ε')
    .replace(/ή/g, 'η')
    .replace(/ί/g, 'ι')
    .replace(/ό/g, 'ο')
    .replace(/ύ/g, 'υ')
    .replace(/ώ/g, 'ω')
    .replace(/ϊ/g, 'ι')
    .replace(/ϋ/g, 'υ')
    .replace(/ΐ/g, 'ι')
    .replace(/ΰ/g, 'υ')
    .replace(/Ά/g, 'Α')
    .replace(/Έ/g, 'Ε')
    .replace(/Ή/g, 'Η')
    .replace(/Ί/g, 'Ι')
    .replace(/Ό/g, 'Ο')
    .replace(/Ύ/g, 'Υ')
    .replace(/Ώ/g, 'Ω');
}

/**
 * Normalize a Greek string for comparison: lowercase + trim + collapse spaces.
 */
export function normalize(str) {
  if (typeof str !== 'string') return '';
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Strict equality after normalization (preserves accents).
 * This is the canonical correctness check.
 */
export function isExactMatch(answer, correct) {
  return normalize(answer) === normalize(correct);
}

/**
 * Near-match: same word but with accent mistakes only.
 * Useful for diagnosing tonismos errors.
 */
export function isAccentOnlyMismatch(answer, correct) {
  if (isExactMatch(answer, correct)) return false;
  return stripAccents(normalize(answer)) === stripAccents(normalize(correct));
}

/**
 * Compute XP for a single question.
 */
export function xpForAnswer({ isCorrect, attemptNumber }) {
  if (!isCorrect) return XP.WRONG;
  if (attemptNumber === 1) return XP.CORRECT_FIRST_ATTEMPT;
  return XP.CORRECT_SECOND_ATTEMPT;
}

/**
 * Bonus XP for a perfect session.
 */
export function perfectSessionBonus(correctCount, totalCount) {
  return correctCount === totalCount ? XP.PERFECT_SESSION_BONUS : 0;
}

// Greek consonants used by countDoubledConsonants — matches digraphs/twin
// consonants like λλ, νν, ττ, ρρ, σσ, μμ, ππ, κκ, γγ, ζζ, etc.
const GREEK_CONSONANT_DOUBLE_REGEX = /([βγδζθκλμνξπρστφχψ])\1/g;

function countDoubledConsonants(str) {
  if (typeof str !== 'string') return 0;
  return (str.match(GREEK_CONSONANT_DOUBLE_REGEX) || []).length;
}

const CHAR_PAIR_TAGS = [
  { pair: ['ο', 'ω'], tag: 'omikron_omega' },
  { pair: ['ε', 'α'], tag: 'epsilon_aita' },
  { pair: ['η', 'ι'], tag: 'ei_i_oi' },
  { pair: ['υ', 'ι'], tag: 'ei_i_oi' },
];

const SUBSTRING_SWAPS = [
  // ου ↔ οι MUST come first: same length, so inferFromCharPairs would
  // otherwise catch the inner υ↔ι diff and misclassify a diphthong-swap
  // (different SOUND: /u/ vs /i/) as an /i/-family spelling mistake.
  { swap: ['ου', 'οι'], tag: 'ou_oi_diphthong' },
  { swap: ['ει', 'ι'], tag: 'ei_i_oi' },
  { swap: ['αι', 'ε'], tag: 'epsilon_aita' },
  { swap: ['οι', 'υ'], tag: 'upsilon_oi' },
  { swap: ['οι', 'ι'], tag: 'ei_i_oi' },
  { swap: ['ει', 'η'], tag: 'ei_i_oi' },
];

function inferFromCharPairs(a, b) {
  if (a.length !== b.length) return null;
  for (let i = 0; i < a.length; i++) {
    if (a[i] === b[i]) continue;
    for (const { pair, tag } of CHAR_PAIR_TAGS) {
      if (
        (a[i] === pair[0] && b[i] === pair[1]) ||
        (a[i] === pair[1] && b[i] === pair[0])
      ) {
        return tag;
      }
    }
  }
  return null;
}

function inferFromSubstringSwap(a, b) {
  for (const { swap: [shortForm, longForm], tag } of SUBSTRING_SWAPS) {
    if (a.replaceAll(shortForm, longForm) === b) return tag;
    if (b.replaceAll(shortForm, longForm) === a) return tag;
    if (a.replaceAll(longForm, shortForm) === b) return tag;
    if (b.replaceAll(longForm, shortForm) === a) return tag;
  }
  return null;
}

// Phonetic family of each Greek vowel. Used by the item-tag-gated
// char-family fallback in `buildErrorTagsForAnswer`: when the deterministic
// stages of `inferMistakeType` find nothing but exactly one character
// differs, we tentatively classify the mistake by the CORRECT character's
// vowel family — BUT only commit to that tag when it's already listed in
// the item's `error_tags`. Without that gate we'd invent confusions the
// item doesn't teach (e.g. μικρό→μικρά is gender_agreement, not the
// epsilon_aita family that the bare 'α' character would suggest).
const CHAR_TO_TAG_FAMILY = {
  ο: 'omikron_omega',
  ω: 'omikron_omega',
  ε: 'epsilon_aita',
  α: 'epsilon_aita',
  η: 'ei_i_oi',
  ι: 'ei_i_oi',
  υ: 'upsilon_oi',
};

function charFamilyHint(selected, correct) {
  if (typeof selected !== 'string' || typeof correct !== 'string') return null;
  const a = stripAccents(normalize(selected));
  const b = stripAccents(normalize(correct));
  if (a.length !== b.length || a === b) return null;
  let onlyDiff = null;
  for (let i = 0; i < a.length; i++) {
    if (a[i] === b[i]) continue;
    if (onlyDiff !== null) return null; // more than one diff — give up
    onlyDiff = i;
  }
  if (onlyDiff === null) return null;
  return CHAR_TO_TAG_FAMILY[b[onlyDiff]] ?? null;
}

// Returns true if positions in `a` and `b` exist where one holds `x` and
// the other holds `y` (in either order). Used by `isTagDiffCoherent` to
// decide whether a vowel-pair tag (ο/ω, ε/α) is consistent with the diff.
function hasCharPairAtAnyPosition(a, b, x, y) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if ((a[i] === x && b[i] === y) || (a[i] === y && b[i] === x)) return true;
  }
  return false;
}

// Returns true when replacing every occurrence of one substring with the
// other in either direction makes the strings equal — used by
// `isTagDiffCoherent` to gate substring-pattern tags (ou_oi_diphthong, …).
function hasSubstringSwap(a, b, x, y) {
  if (a.replaceAll(x, y) === b) return true;
  if (a.replaceAll(y, x) === b) return true;
  if (b.replaceAll(x, y) === a) return true;
  if (b.replaceAll(y, x) === a) return true;
  return false;
}

/**
 * Decide whether a character-level error tag could PHYSICALLY describe the
 * diff between `selected` and `correct`. The item's `error_tags` describe
 * the LESSON the word teaches, not the kid's actual mistake — so a word
 * tagged `tonismos` is only ABOUT τονισμός; if the kid wrote a totally
 * different letter, surfacing a τόνος rule misleads.
 *
 * Returns `true` when the tag is consistent with the diff OR when the tag
 * is not a character-level pattern we can verify (afonos_h, gender_*,
 * verb_ending, compound_words, spelling_attention) — those pass through.
 *
 * Examples:
 *   isTagDiffCoherent('tonismos',  'θρονίο', 'θρανίο') === false  // letter diff, no accent shift
 *   isTagDiffCoherent('tonismos',  'καρδια', 'καρδιά') === true   // accent-only
 *   isTagDiffCoherent('dipla_sygfona', 'κόκκινον', 'κόκκινο') === false // κκ unchanged
 *   isTagDiffCoherent('omikron_omega', 'γνώση',  'γνωση')  === false // accent-only (no ο↔ω)
 *   isTagDiffCoherent('ei_i_oi',   'τίγρη',  'τίγρης') === false // trailing-letter diff
 */
export function isTagDiffCoherent(tag, selected, correct) {
  const s = normalize(selected);
  const c = normalize(correct);
  if (!s || !c || s === c) return true;

  const sNa = stripAccents(s);
  const cNa = stripAccents(c);

  switch (tag) {
    case 'tonismos':
      return sNa === cNa;
    case 'dipla_sygfona':
      return countDoubledConsonants(sNa) !== countDoubledConsonants(cNa);
    case 'omikron_omega':
      return hasCharPairAtAnyPosition(sNa, cNa, 'ο', 'ω');
    case 'epsilon_aita':
      return hasCharPairAtAnyPosition(sNa, cNa, 'ε', 'α');
    case 'ei_i_oi':
    case 'upsilon_oi':
      return isIFamilySwap(computeDiffChars(selected, correct));
    case 'ou_oi_diphthong':
      return hasSubstringSwap(sNa, cNa, 'ου', 'οι');
    // Tags below describe non-grapheme patterns (silent η at word
    // boundaries, declension, syntax, morphology). The diff alone can't
    // prove them inconsistent, so let them through.
    case 'afonos_h':
    case 'gender_agreement':
    case 'verb_ending':
    case 'compound_words':
    case 'spelling_attention':
      return true;
    default:
      return true;
  }
}

/**
 * Infer the most likely error tag from a selected (wrong) answer compared
 * against the correct one. Used by exercises so feedback reflects the actual
 * mistake rather than the first generic `error_tags` entry on the item.
 *
 * Returns a known error tag string or null when no confident match.
 *
 * This function only commits to HIGH-CONFIDENCE deterministic patterns:
 *   1. Pure accent mismatch       → 'tonismos'
 *   2. Double-consonant mismatch  → 'dipla_sygfona'
 *   3. Diphthong substring swap   → ου↔οι, ει↔ι, αι↔ε, οι↔υ, οι↔ι, ει↔η
 *   4. Per-position char pair     → ο/ω, ε/α, η/ι, υ/ι
 *
 * Substring swaps run BEFORE per-position char pairs so that diphthong-level
 * confusions (e.g. ού→οί in παπούτσι→παποίτσι) are classified by the whole
 * diphthong's meaning, not by the inner single-character diff.
 *
 * Lower-confidence guesses (single-char diffs that don't fit any of the
 * above) are handled by `buildErrorTagsForAnswer`, which has access to the
 * item's `error_tags` and only commits when the guess matches an item tag.
 */
export function inferMistakeType(selected, correct) {
  if (typeof selected !== 'string' || typeof correct !== 'string') return null;
  const s = normalize(selected);
  const c = normalize(correct);
  if (!s || !c || s === c) return null;

  const sNoAccent = stripAccents(s);
  const cNoAccent = stripAccents(c);

  if (sNoAccent === cNoAccent) return 'tonismos';

  if (countDoubledConsonants(sNoAccent) !== countDoubledConsonants(cNoAccent)) {
    return 'dipla_sygfona';
  }

  return (
    inferFromSubstringSwap(sNoAccent, cNoAccent) ||
    inferFromCharPairs(sNoAccent, cNoAccent) ||
    null
  );
}

// All Greek graphemes that spell the /i/ sound. Used by computeDiffChars's
// consumers (resolveRuleForFeedback) to decide whether a wrong↔correct diff
// belongs to the /ι/ family and therefore should surface the dynamic rule.
const I_FAMILY_SPELLINGS = new Set(['ι', 'η', 'υ', 'ει', 'οι']);

export function isIFamilySwap(diffChars) {
  if (!diffChars || typeof diffChars !== 'object') return false;
  const { wrong, correct } = diffChars;
  return (
    typeof wrong === 'string' &&
    typeof correct === 'string' &&
    I_FAMILY_SPELLINGS.has(wrong) &&
    I_FAMILY_SPELLINGS.has(correct)
  );
}

// Greek consonants that can appear as a doubled pair (λλ, σσ, νν, …).
// Used by computeDiffChars and isDiplaSygfonaSwap to recognise mistakes
// where a child added or dropped one half of a doubled-consonant pair
// (θάλασσα → θάλλασσα, γράμμα → γράμα).
const GREEK_CONSONANTS = 'βγδζθκλμνξπρστφχψ';
const GREEK_CONSONANT_SET = new Set(GREEK_CONSONANTS);

// Returns the doubled-consonant swap that explains the diff, or null when
// no single-consonant collapse turns one string into the other. The check
// runs both directions:
//
//   findDoubledConsonantSwap('θαλλασσα','θαλασσα') → { wrong:'λλ', correct:'λ' }
//   findDoubledConsonantSwap('γραμα','γραμμα')     → { wrong:'μ',  correct:'μμ' }
//
// A word with multiple independent doubled-letter mistakes (e.g. extra λλ
// AND missing σσ in the same answer) returns null — the panel falls back
// to the static dipla rule message instead of misreporting a single swap.
function findDoubledConsonantSwap(a, b) {
  for (const c of GREEK_CONSONANTS) {
    const doubled = c + c;
    if (a.includes(doubled) && a.replaceAll(doubled, c) === b) {
      return { wrong: doubled, correct: c };
    }
    if (b.includes(doubled) && b.replaceAll(doubled, c) === a) {
      return { wrong: c, correct: doubled };
    }
  }
  return null;
}

/**
 * True when the diff is a clean dipla-sygfona swap: exactly one side has
 * an extra doubled consonant (λλ vs λ, σσ vs σ, …). Used by
 * resolveRuleForFeedback to upgrade a generic dipla_sygfona rule to the
 * dynamic version whose template substitutes the actual pair.
 */
export function isDiplaSygfonaSwap(diffChars) {
  if (!diffChars || typeof diffChars !== 'object') return false;
  const { wrong, correct } = diffChars;
  if (typeof wrong !== 'string' || typeof correct !== 'string') return false;
  if (
    wrong.length === 2 &&
    correct.length === 1 &&
    wrong === correct + correct
  ) {
    return GREEK_CONSONANT_SET.has(correct);
  }
  if (
    wrong.length === 1 &&
    correct.length === 2 &&
    correct === wrong + wrong
  ) {
    return GREEK_CONSONANT_SET.has(wrong);
  }
  return false;
}

/**
 * True when the diff is a clean ο↔ω swap (single-character vowel swap
 * between omikron and omega). Used by resolveRuleForFeedback to upgrade
 * the static `g{N}_rule_o_omega` rule — whose pedagogical text talks
 * about word ENDINGS for verbs/nouns/adjectives — to the dynamic ο/ω rule
 * whose template substitutes the actual letters the kid wrote (works for
 * any position in the word, including the middle of nouns like μπαλόνι).
 */
export function isOmikronOmegaSwap(diffChars) {
  if (!diffChars || typeof diffChars !== 'object') return false;
  const { wrong, correct } = diffChars;
  if (typeof wrong !== 'string' || typeof correct !== 'string') return false;
  const a = stripAccents(wrong);
  const b = stripAccents(correct);
  return (
    (a === 'ο' && b === 'ω') || (a === 'ω' && b === 'ο')
  );
}

/**
 * Return the SHORT substrings that differ between the child's answer and
 * the correct one, when the difference is a clean swap. Used to feed
 * `{wrong}` / `{correct}` placeholders in dynamic rule templates.
 *
 *   computeDiffChars('βούτηρο', 'βούτυρο') → { wrong: 'η', correct: 'υ' }
 *   computeDiffChars('βούτοιρο','βούτυρο') → { wrong: 'οι', correct: 'υ' }
 *   computeDiffChars('αριθμό',  'αριθμός') → null  (trailing-letter; no clean swap)
 *   computeDiffChars('σκύλος',  'γάτα')    → null  (unrelated words)
 *
 * Returns null when the diff isn't a deterministic single-char-or-digraph
 * swap. Pure: no React, no side effects.
 */
export function computeDiffChars(selected, correct) {
  if (typeof selected !== 'string' || typeof correct !== 'string') return null;
  const s = normalize(selected);
  const c = normalize(correct);
  if (!s || !c || s === c) return null;

  const sNoAccent = stripAccents(s);
  const cNoAccent = stripAccents(c);
  if (sNoAccent === cNoAccent) return null; // accent-only diff → no chars

  // Substring swap first (digraphs like ου↔οι, ει↔ι, οι↔υ, …) so a
  // diphthong-level swap isn't mis-reported as a single-char diff.
  for (const { swap: [a, b] } of SUBSTRING_SWAPS) {
    if (sNoAccent.replaceAll(a, b) === cNoAccent) {
      return { wrong: a, correct: b };
    }
    if (sNoAccent.replaceAll(b, a) === cNoAccent) {
      return { wrong: b, correct: a };
    }
  }

  // Doubled-consonant swap (λλ↔λ, σσ↔σ, …). Runs before the single-char
  // path so a missing/extra doubled letter (θάλασσα↔θάλλασσα — length
  // diff) gets reported as a clean dipla-pair swap.
  const dipla = findDoubledConsonantSwap(sNoAccent, cNoAccent);
  if (dipla) return dipla;

  // Single-char diff at exactly one position.
  if (sNoAccent.length === cNoAccent.length) {
    let onlyDiff = null;
    for (let i = 0; i < sNoAccent.length; i++) {
      if (sNoAccent[i] === cNoAccent[i]) continue;
      if (onlyDiff !== null) return null;
      onlyDiff = i;
    }
    if (onlyDiff !== null) {
      return { wrong: sNoAccent[onlyDiff], correct: cNoAccent[onlyDiff] };
    }
  }

  return null;
}

/**
 * Build the ordered error-tag list to pass through `onAnswer` from an exercise
 * component after a wrong answer. The inferred tag (from selected vs correct)
 * is placed first so it is preferred by rule lookup and hint selection over
 * the item's generic `error_tags`.
 *
 * When `inferMistakeType` returns null but exactly one character differs,
 * we try a lower-confidence char-family hint (the correct char's vowel
 * family) — but only commit to it when it's ALREADY in the item's
 * `error_tags`. This catches cases like αστυνομικός→αστυνυμικός (correct
 * 'ο' → omikron_omega, which IS in the item tags) while avoiding spurious
 * tags on cases like μικρά→μικρό (correct 'α' would map to epsilon_aita,
 * but the item's real tag is gender_agreement — without the gate, the user
 * would see an irrelevant rule about ε/αι).
 *
 * Coherence gate: the item's `error_tags` describe the LESSON the word
 * teaches, not the kid's actual mistake. Tags that physically can't
 * describe the diff (e.g. item says `tonismos` but no accent shift
 * happened — see θρανίο→θρονίο, or trailing-letter diffs like
 * αριθμό→αριθμός) are filtered out via `isTagDiffCoherent`. This
 * generalises the previous trailing-letter guard to every character-level
 * pattern. An empty result here is a deliberate signal to the resolver to
 * surface the generic spelling-attention rule instead of a misleading
 * canonical one.
 */
export function buildErrorTagsForAnswer(selected, correct, itemTags = []) {
  const inferred = inferMistakeType(selected, correct);
  const baseRaw = Array.isArray(itemTags) ? itemTags.filter((t) => !!t) : [];

  // Inferred and family-hint paths both produce a topTag derived from the
  // kid's actual mistake. The item's other tags stay in place as secondary
  // context (the resolver only ever picks rules via topTag anyway).
  if (inferred) return [...new Set([inferred, ...baseRaw])];

  const familyHint = charFamilyHint(selected, correct);
  if (familyHint && baseRaw.includes(familyHint)) {
    return [...new Set([familyHint, ...baseRaw])];
  }

  // Default path: nothing was confidently inferred from the diff. The
  // item's `error_tags` describe the LESSON the word teaches, NOT the
  // kid's actual mistake — so we coherence-filter them. A tag survives
  // only when its character-level pattern is physically present in the
  // diff. When everything is filtered out, the empty result deliberately
  // signals the resolver to surface the generic spelling-attention rule
  // instead of a misleading canonical one (e.g. avoids showing "Τονισμός
  // λέξεων" for θρανίο→θρονίο, where no accent shift happened).
  return [
    ...new Set(
      baseRaw.filter((t) => isTagDiffCoherent(t, selected, correct))
    ),
  ];
}
